// apps/patient/src/pages/classes/ClassDetail.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users, MapPin, Clock, Calendar, CheckCircle } from 'lucide-react';
import { patientApiService } from '../../services/api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const ClassDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [definition, setDefinition] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [enrolledSessionIds, setEnrolledSessionIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [defs, sess, myEnrollments] = await Promise.all([
        patientApiService.getAvailableClasses(),
        patientApiService.getClassSessions(id!, {}),
        patientApiService.getMyClassEnrollments().catch(() => []),
      ]);
      const def = (defs || []).find((d: any) => d.id === id);
      setDefinition(def || null);
      setSessions(sess || []);
      const enrolledIds = new Set<string>(
        (myEnrollments || [])
          .filter((e: any) => ['enrolled', 'checked_in', 'waitlisted'].includes(e.status))
          .map((e: any) => e.class_session_id)
          .filter(Boolean)
      );
      setEnrolledSessionIds(enrolledIds);
    } catch (err) {
      console.error('Failed to load class:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (sessionId: string) => {
    setEnrolling(sessionId);
    try {
      const paymentTypes = definition?.allowed_payment_types || [];
      const paymentMethod = paymentTypes.includes('free') ? 'free' :
        paymentTypes.includes('subscription') ? 'subscription' :
        paymentTypes.includes('package_credit') ? 'package_credit' :
        'single_purchase';

      if (paymentMethod === 'single_purchase' && definition?.single_purchase_price > 0) {
        // Enrolls and creates the class invoice; pay it through the ordinary invoice route
        const result = await patientApiService.purchaseClassEnrollment(sessionId);
        if (result?.invoice_id) {
          navigate(`/billing/pay/${result.invoice_id}`);
          return;
        }
        // Waitlisted: no seat yet, so no invoice yet (created on promotion)
        setSuccessMessage('Added to the waitlist. You will be invoiced when a spot opens.');
        setTimeout(() => setSuccessMessage(''), 4000);
        await loadData();
        return;
      }

      await patientApiService.enrollInClass({
        class_session_id: sessionId,
        payment_method: paymentMethod,
      });
      setSuccessMessage('Successfully enrolled!');
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to enroll');
    } finally {
      setEnrolling(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!definition) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <p className="text-gray-500">Class not found.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {/* Header */}
      <button onClick={() => navigate('/classes')} className="flex items-center gap-2 text-gray-500 mb-4">
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back to Classes</span>
      </button>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{definition.name}</h1>
        {definition.description && (
          <p className="text-gray-600 mb-4">{definition.description}</p>
        )}
        <div className="grid grid-cols-2 gap-3 text-sm text-gray-500">
          {definition.provider && (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              <span>Instructor: {definition.provider.first_name} {definition.provider.last_name}</span>
            </div>
          )}
          {definition.location && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span>{definition.location.name}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span>{definition.recurrence_pattern?.days?.map((d: number) => DAYS[d]).join(', ')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span>{definition.start_time} - {definition.end_time}</span>
          </div>
        </div>
        {definition.allowed_payment_types?.includes('single_purchase') && (definition.single_purchase_price > 0 || definition.series_price > 0) && (
          <div className="mt-4 flex flex-wrap gap-4 text-sm">
            {definition.single_purchase_price > 0 && (
              <span className="font-medium text-primary">${Number(definition.single_purchase_price).toFixed(2)} per session</span>
            )}
            {definition.series_price > 0 && (
              <span className="font-medium text-gray-700">Whole series: ${Number(definition.series_price).toFixed(2)} (ask the front desk to enroll you)</span>
            )}
          </div>
        )}
      </div>

      {/* Sessions */}
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Upcoming Sessions</h2>
      {sessions.length === 0 ? (
        <p className="text-gray-500 text-sm">No upcoming sessions available.</p>
      ) : (
        <div className="space-y-3">
          {sessions.map((session: any) => {
            const spotsLeft = session.capacity - session.enrolled_count;
            const isFull = spotsLeft <= 0;
            const isEnrolled = enrolledSessionIds.has(session.id);

            return (
              <div key={session.id} className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">
                    {new Date(session.session_date + 'T00:00').toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric'
                    })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {session.start_time} - {session.end_time}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {spotsLeft > 0 ? `${spotsLeft} spots left` : 'Full'}
                    {session.waitlist_count > 0 && ` (${session.waitlist_count} waitlisted)`}
                  </div>
                </div>
                {isEnrolled ? (
                  <button
                    onClick={() => navigate('/my-classes')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg font-medium bg-green-100 text-green-700 hover:bg-green-200"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Enrolled
                  </button>
                ) : (
                  <button
                    onClick={() => handleEnroll(session.id)}
                    disabled={enrolling === session.id}
                    className={`px-4 py-2 text-sm rounded-lg font-medium ${
                      isFull
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-primary text-white hover:bg-primary/90'
                    } disabled:opacity-50`}
                  >
                    {enrolling === session.id ? 'Enrolling...' : isFull ? 'Join Waitlist' : 'Enroll'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassDetail;
